import { inject, Injectable } from "@angular/core"
import { OkxWebsocketClient } from "../../../market"
import { filter, first, Observable } from "rxjs"
import { Order, OrderSide, OrderType, PositionSide } from "../../../model/trade"
import { isWsDataOrderAlgoEvent, isWsDataOrderEvent, WsDataEvent } from "libs/okx-api/src"
import { parseFloatSafe, parseIntSafe } from "src/app/core/func"
import { BoardId } from "src/app/core/model/common"
import { InstrumentStorageService } from "../../instrument-storage.service"

@Injectable({
  providedIn: 'root'
})
export class OkxOrderHandler {

  private instrumentStorage = inject(InstrumentStorageService)

  listen(ws: OkxWebsocketClient) {
    return new Observable<Order>(subscriber => {
      const f = (data: WsDataEvent) => {
        if (isWsDataOrderEvent(data)) {
          data.data.forEach(row => {
            this.instrumentStorage.listen('okx', 'swap', row.instId).pipe(
              filter(instrument => instrument != undefined),
              first(),
            ).subscribe(instrument => {
              const remainingQuantity = parseFloatSafe(row.sz) - parseFloatSafe(row.accFillSz)
              const remainingVolume = parseFloatSafe(row.px) * remainingQuantity * instrument.lotSize

              const order: Order = {
                createdTimestamp: parseIntSafe(row.cTime),
                updateTimestamp: parseIntSafe(row.uTime),
                type: row.ordType === 'limit' ? OrderType.limit : (row.ordType === 'market' ? OrderType.market : OrderType.unknown),
                status: row.state,
                market: 'okx',
                board: row.instType.toLowerCase() as BoardId,
                symbol: row.instId,
                price: row.px,
                quantity: row.sz,
                volume: parseFloatSafe(row.px) * parseFloatSafe(row.sz) * instrument.lotSize,
                remainingQuantity: remainingQuantity,
                remainingVolume: remainingVolume,
                side: row.side === 'buy' ? OrderSide.buy : OrderSide.sell,
                positionSide: row.posSide === 'long' ? PositionSide.long : row.posSide === 'short' ? PositionSide.short : PositionSide.both,
                orderId: row.ordId,
                clientOrderId: row.clOrdId,
                isStop: ['conditional', 'oco', 'chase', 'trigger', 'move_order_stop', 'twap'].includes(row.ordType),
                isClosePosition: row.reduceOnly == 'true',
              }
              subscriber.next(order)
            })
          })
        }
        else if (isWsDataOrderAlgoEvent(data)) {
          data.data.forEach(row => {
            const order: Order = {
              createdTimestamp: parseIntSafe(row.cTime),
              updateTimestamp: parseIntSafe(row.uTime),
              type: row.ordType === 'limit' ? OrderType.limit : (row.ordType === 'market' ? OrderType.market : OrderType.unknown),
              status: row.state,
              market: 'okx',
              board: row.instType.toLowerCase() as BoardId,
              symbol: row.instId,
              price: row.slTriggerPx,
              quantity: 0,
              volume: 0,
              remainingQuantity: 0,
              remainingVolume: 0,
              side: row.side === 'buy' ? OrderSide.buy : OrderSide.sell,
              positionSide: row.posSide === 'long' ? PositionSide.long : row.posSide === 'short' ? PositionSide.short : PositionSide.both,
              orderId: row.algoId,
              clientOrderId: row.algoClOrdId,
              isStop: true,
              isClosePosition: row.reduceOnly == 'true',
            }
            subscriber.next(order)
          })
        }
      }

      ws.on('update', f)
      return () => ws.off('update', f)
    })
  }

}
